"use client";

import type { PurchaseProduct } from "../utils/toCartProduct";
import { useCart } from "@features/cart/hooks/useCart";
import { useEffect, useState } from "react";

import { isOversellableProduct } from "../utils/isOversellableProduct";
import { useProductQuantity } from "./useProductQuantity";

export function useProductForm(product: PurchaseProduct) {
  const { items } = useCart();
  const variantKey = product.partNo;
  const oversellable = isOversellableProduct(product.itemStatus, product.flags);
  const [recentlyAdded, setRecentlyAdded] = useState(false);

  const cartQuantity =
    items.find((item) => item.partNo === variantKey)?.quantity ?? 0;
  const onHand = product.stockOnHand;
  const maxQuantity =
    typeof onHand === "number" ? Math.max(1, onHand - cartQuantity) : undefined;
  const { quantity, decrease, increase, setQuantity } = useProductQuantity(
    oversellable ? undefined : maxQuantity,
  );
  const atMax =
    typeof onHand === "number" &&
    cartQuantity + quantity >= onHand &&
    !oversellable;
  const disabled =
    !variantKey ||
    (typeof onHand === "number" && cartQuantity >= onHand && !oversellable);

  useEffect(() => {
    if (!recentlyAdded) return;
    const timeout = window.setTimeout(() => setRecentlyAdded(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [recentlyAdded]);

  return {
    atMax,
    quantity,
    disabled,
    variantKey,
    maxQuantity,
    oversellable,
    recentlyAdded,
    decrease,
    increase,
    setQuantity,
    setRecentlyAdded,
  };
}
